import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckBadgeIcon } from "@heroicons/react/24/outline";
import { HelpTitle } from "../../components/Help/HelpButton";
import TasksGuide from "../../components/Help/guides/TasksGuide";
import TaskCard from "../../components/QuestionFolders/tasks/TaskCard";
import { taskErrorMessage } from "../../components/QuestionFolders/tasks/taskModel";
import { getMyTasks, getQuestionFolders } from "../../services/questionService";
import { folderPath } from "../../utils/folders";
import { isCanceledError } from "../../utils/examError";
import { focusRing, Spinner } from "../Exams/TestBuilder/ui";

const TABS = [
  { key: "active", done: false },
  { key: "done", done: true },
];

/**
 * My tasks (TZ 2.2): the tasks given to the user in every subject (GET my_tasks/), the ones in work or the done ones.
 * A write task leads to a new question in its subject; a review task to its questions in the bank (those left first).
 */
export default function MyTasks() {
  const [t] = useTranslation();
  const [tab, setTab] = useState("active");
  const [tasks, setTasks] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [byUuid, setByUuid] = useState(() => new Map());

  // The folders, for "ТАТ · Biology" over each task.
  useEffect(() => {
    const controller = new AbortController();
    getQuestionFolders({ signal: controller.signal })
      .then((list) => setByUuid(new Map((Array.isArray(list) ? list : []).map((f) => [f.uuid, f]))))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setTasks(null);
    setError("");
    getMyTasks({ done: TABS.find((item) => item.key === tab).done, signal: controller.signal })
      .then((list) => setTasks(Array.isArray(list) ? list : []))
      .catch((err) => {
        if (isCanceledError(err)) return;
        setTasks([]);
        setError(taskErrorMessage(t, err, "folders.tasks.load_failed"));
      });
    return () => controller.abort();
  }, [tab, attempt, t]);

  const pathOf = useMemo(
    () => (task) => {
      const folder = byUuid.get(task.folder?.uuid);
      if (folder) return folderPath(folder, byUuid);
      const exam = typeof task.folder?.parent === "object" ? task.folder.parent?.name : "";
      return [exam, task.folder?.name].filter(Boolean).join(" · ");
    },
    [byUuid]
  );

  let body;
  if (tasks === null) {
    body = (
      <div role="status" className="flex items-center justify-center gap-3 py-16 text-sm text-slate-500">
        <Spinner className="h-5 w-5" />
        {t("folders.tasks.loading")}
      </div>
    );
  } else if (error) {
    body = (
      <div className="flex flex-col items-center gap-3 py-14 text-center">
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
        <button
          type="button"
          onClick={() => setAttempt((n) => n + 1)}
          className={`inline-flex h-10 items-center rounded-lg bg-white px-4 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 max-sm:h-11 ${focusRing}`}
        >
          {t("folders.tasks.retry")}
        </button>
      </div>
    );
  } else if (tasks.length === 0) {
    body = (
      <div className="mx-auto max-w-md py-14 text-center">
        <CheckBadgeIcon className="mx-auto h-10 w-10 text-slate-300" aria-hidden="true" />
        <p className="mt-3 text-sm font-medium text-slate-900">{tab === "active" ? t("my_tasks.empty_active") : t("my_tasks.empty_done")}</p>
        {tab === "active" && <p className="mt-1 text-sm text-slate-500">{t("my_tasks.empty_hint")}</p>}
      </div>
    );
  } else {
    body = (
      <ul className="space-y-3">
        {tasks.map((task) => (
          <li key={task.uuid}>
            <TaskCard task={task} folderPath={pathOf(task)} showFolder own />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-5 motion-safe:animate-fade-in">
      <header>
        {/* The guide's "?" right after the title. */}
        <HelpTitle as="h1" helpId="tasks" helpTitle={t("help.tasks.title")} guide={<TasksGuide />}>
          {t("nav.my_tasks")}
        </HelpTitle>
        <p className="mt-1 text-sm text-slate-500">{t("my_tasks.subtitle")}</p>
      </header>
      <div role="tablist" aria-label={t("nav.my_tasks")} className="inline-flex self-start rounded-lg bg-slate-100 p-1 max-sm:w-full">
        {TABS.map(({ key }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`min-h-[2.25rem] rounded-md px-4 text-sm font-medium transition duration-200 max-sm:min-h-11 max-sm:flex-1 ${focusRing} ${
              tab === key ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {t(`my_tasks.tab_${key}`)}
          </button>
        ))}
      </div>
      <section aria-live="polite">{body}</section>
    </div>
  );
}
